<script setup lang="ts">
import type { MessageSchema } from '@/i18n';
import type { Dish } from '@/types/dish';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import DishTagsInDialog from '@/components/Menus/MenuDishViewComponents/PlanDishFromDialog/DishTagsInDialog.vue';
import MyRecipesInDialogView from '@/components/Menus/MenuDishViewComponents/PlanDishFromDialog/MyRecipesInDialog.vue';
import FavoritesView from '@/views/MyRecipes/FavoritesView.vue';
import GeneratedTitlesView from '@/views/MyRecipes/GeneratedTitlesView.vue';
import GenerationHistoryView from '@/views/MyRecipes/GenerationHistoryView.vue';
import MyScansView from '@/views/MyRecipes/MyScansView.vue';
import TagView from '@/views/MyRecipes/TagView.vue';
import Button from 'primevue/button';
import Sidebar from 'primevue/sidebar';
import { useSafeArea } from '@/composables/useSafeArea';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { bottomSafeArea } = useSafeArea();
const visible = defineModel<boolean>('visible', { required: true });

const emit = defineEmits<{
  (e: 'dish-selected', dish: Dish): void;
  (e: 'dish-title-selected', dishTitle: string): void;
}>();

// State for navigation
const category = ref('');
const subCategory = ref('');
const showTagView = ref(false);

// Computed properties
const dialogTitle = computed(() => {
  if (category.value === 'history') {
    return t('menu.dish.addFromHistory');
  } else if (category.value === 'favorites') {
    return t('menu.dish.addFromFavorites');
  } else if (category.value === 'scans') {
    return t('menu.dish.addFromScans');
  } else if (category.value === 'tags') {
    return t('menu.dish.addFromTags');
  } else {
    return t('menu.dish.addFromRecipes');
  }
});

const selectCategory = (newCategory: string) => {
  category.value = newCategory;
  if (newCategory === 'tags') {
    showTagView.value = true;
  } else {
    showTagView.value = false;
  }
};

const selectTag = (tag: string) => {
  subCategory.value = tag;
  category.value = 'tags';
  showTagView.value = false;
};

const selectDish = (dish: Dish) => {
  emit('dish-selected', dish);
  visible.value = false;
};

const selectDishTitle = (dish: Dish) => {
  emit('dish-title-selected', dish.name);
  visible.value = false;
};

const goBack = () => {
  if (subCategory.value) {
    // If there's a subcategory, clear it and go back to tags view
    subCategory.value = '';
    showTagView.value = true;
  } else if (category.value) {
    // If there's only a category, clear it and go back to main view
    category.value = '';
    showTagView.value = false;
  }
};

// Reset state when dialog closes
watch(visible, (newValue) => {
  if (!newValue) {
    category.value = '';
    subCategory.value = '';
    showTagView.value = false;
  }
});
</script>

<template>
  <Sidebar
    v-model:visible="visible"
    :header="dialogTitle"
    position="bottom"
    style="height: 90%"
    :block-scroll="true"
    class="w-full md:w-3/4 lg:w-1/2"
  >
    <div class="flex h-full flex-col">
      <!-- Back Button -->
      <div v-if="category && !showTagView" class="mb-4 flex items-center justify-start">
        <Button
          rounded
          link
          icon="pi pi-chevron-left"
          class="text-xl"
          :pt="{ root: { class: '!w-8 !h-8' } }"
          :pt-options="{ mergeProps: true }"
          @click="goBack"
        />
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-auto" :style="{ paddingBottom: bottomSafeArea }">
        <MyRecipesInDialogView v-if="!category && !showTagView" :exclude-categories="['planned', 'titles']" @select-category="selectCategory" />

        <DishTagsInDialog v-if="showTagView" @select-tag="selectTag" @select-category="selectCategory" />

        <GenerationHistoryView v-if="category === 'history'" select-mode @dish-clicked="selectDish" />
        <MyScansView v-if="category === 'scans'" select-mode @dish-clicked="selectDish" />
        <FavoritesView v-if="category === 'favorites'" select-mode @dish-clicked="selectDish" />
        <TagView v-if="category === 'tags' && subCategory" select-mode :tag-id="Number(subCategory)" @dish-clicked="selectDish" />
        <GeneratedTitlesView v-if="category === 'titles'" @add-dish-title-to-plan="selectDishTitle" />
      </div>
    </div>
  </Sidebar>
</template>
